import api from './api';

/**
 * Vertrouwenspersoon (discipline 7) — /api/vertrouwenspersoon.
 * Zichtbaarheidsuren (GPS-Grendel) en de Waakvlam-constructie (12-uurs SLA).
 */

export interface WaakvlamTier {
  tier: 1 | 2 | 3;
  naam: string;
  min: number;
  max: number | null;
  maandfee: number;
}

export interface WerkgeverOverzicht {
  waakvlam: {
    tiers: WaakvlamTier[];
    sla_uren: number;
    split: { vp_pct: number; platform_pct: number };
    salarisverwerkers: string[];
    bedrijfsgrootte: { aantal: number; bron: 'PAYROLL' | 'PROFIEL' } | null;
    tier: WaakvlamTier | null;
    voorwaarden: { versie: string; tekst: string; hash: string } | null;
    kandidaten: Array<{ professional_id: string; naam: string; sla_akkoord: boolean; stripe_geverifieerd: boolean }>;
    abonnement: {
      id: string;
      status: 'PENDING_MANDATE' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED';
      tier: number;
      maandfee: number;
      medewerkers_aantal: number;
      medewerkers_bron: 'PAYROLL' | 'PROFIEL';
      salarisverwerker: string | null;
      vertrouwenspersoon: { id: string; naam: string };
      actief_sinds: string | null;
      opgezegd_op: string | null;
    } | null;
    perioden: Array<{ periode: string; bruto: number; sla: 'GEHAALD' | 'OVERSCHREDEN'; teruggestort: boolean; teruggestort_op: string | null }>;
  };
  zichtbaarheid: {
    mandaten: Array<{
      id: string;
      status: 'ACTIEF' | 'INGETROKKEN';
      vestiging: { id: string; naam: string; adres: string | null; postcode: string | null; plaats: string | null; bag_adres: string | null };
      vertrouwenspersoon: { id: string; naam: string };
      max_minuten_per_maand: number;
      uurtarief: number;
      periode: string;
      gebruikt_ms: number;
      lopende_ronde_sinds: string | null;
    }>;
    vestigingen: Array<{ id: string; naam: string; adres: string | null; postcode: string | null; plaats: string | null }>;
    vertrouwenspersonen: Array<{ professional_id: string; naam: string; uurtarief: number | null }>;
  };
}

export interface VpOverzicht {
  waakvlam: {
    sla_uren: number;
    voorwaarden: { versie: string; tekst: string; hash: string; geaccepteerd_op: string | null };
    abonnementen: Array<{ id: string; opdrachtgever: string; status: string; tier: number; vp_per_maand: number; actief_sinds: string | null; opgezegd_op: string | null }>;
    open_timers: Array<{ id: string; conversation_id: string; gestart_op: string; deadline: string }>;
    perioden: Array<{ periode: string; vp_aandeel: number; status: string; uitbetaald_op: string | null; bedrijfsnaam: string | null }>;
    gesprekken: Array<{
      conversation_id: string;
      onderwerp: string;
      laatste_bericht: string | null;
      meldingscode: string;
      status: string;
      open_timer: { id: string; deadline: string } | null;
    }>;
  };
  zichtbaarheid: {
    radius_m: number;
    max_nauwkeurigheid_m: number;
    ping_interval_s: number;
    periode: string;
    mandaten: Array<{
      id: string;
      opdrachtgever: string;
      vestiging: { naam: string; adres: string | null; postcode: string | null; plaats: string | null; bag_adres: string | null };
      max_minuten_per_maand: number;
      uurtarief: number;
      gebruikt_ms: number;
      resterend_ms: number;
    }>;
    actieve_ronde: { id: string; mandaat_id: string; check_in_at: string; laatst_op_locatie_at: string; referentie_code: string } | null;
    recente_rondes: Array<{
      id: string;
      referentie_code: string;
      check_in_at: string;
      check_out_at: string;
      eind_bron: 'CHECK_OUT' | 'LAATSTE_PING';
      duur_ms: number;
      gefactureerde_ms: number;
      bedrag: number;
      invoice_id: string | null;
      vestiging: string;
    }>;
  };
}

export interface Positie {
  lat: number;
  lon: number;
  nauwkeurigheid: number;
}

export interface MedewerkerVpStatus {
  sla_uren: number;
  vertrouwenspersonen: Array<{ professional_id: string; naam: string; foto_url: string | null; waakvlam: boolean; conversation_id: string | null }>;
}

// ─── Opdrachtgever ────────────────────────────────────────────────────────────

export const fetchWerkgeverOverzicht = async (): Promise<WerkgeverOverzicht> =>
  (await api.get('/vertrouwenspersoon/employer/overzicht')).data.data;

export const activeerWaakvlam = async (invoer: { professional_id: string; salarisverwerker: string | null; voorwaarden_hash: string }) =>
  (await api.post('/vertrouwenspersoon/employer/waakvlam/activeer', invoer)).data.data as { status: string; checkoutUrl: string | null };

export const zegWaakvlamOp = async () => (await api.post('/vertrouwenspersoon/employer/waakvlam/opzeggen')).data;

export const maakMandaat = async (invoer: { location_id: string; professional_id: string; max_minuten_per_maand: number; uurtarief: number }) =>
  (await api.post('/vertrouwenspersoon/employer/mandaten', invoer)).data.data as { id: string; bag_adres: string };

export const trekMandaatIn = async (id: string) => (await api.post(`/vertrouwenspersoon/employer/mandaten/${id}/intrekken`)).data;

// ─── Professional ─────────────────────────────────────────────────────────────

export const fetchVpOverzicht = async (): Promise<VpOverzicht> => (await api.get('/vertrouwenspersoon/professional/overzicht')).data.data;

export const accepteerWaakvlamVoorwaarden = async (voorwaarden_hash: string) =>
  (await api.post('/vertrouwenspersoon/professional/waakvlam/akkoord', { voorwaarden_hash })).data;

export const trekWaakvlamAkkoordIn = async () => (await api.post('/vertrouwenspersoon/professional/waakvlam/akkoord/intrekken')).data;

export const checkIn = async (mandaat_id: string, p: Positie) =>
  (await api.post('/vertrouwenspersoon/professional/zichtbaarheid/check-in', { mandaat_id, ...p })).data.data as {
    id: string;
    check_in_at: string;
    afstand_m: number;
    status: string;
  };

export const pingRonde = async (rondeId: string, p: Positie) =>
  (await api.post(`/vertrouwenspersoon/professional/zichtbaarheid/${rondeId}/ping`, p)).data.data as { op_locatie: boolean; afstand_m: number };

export const checkOut = async (rondeId: string, p: Positie | null) =>
  (await api.post(`/vertrouwenspersoon/professional/zichtbaarheid/${rondeId}/check-out`, p ?? {})).data.data as {
    duur_ms: number;
    gefactureerde_ms: number;
    eind_bron: 'CHECK_OUT' | 'LAATSTE_PING';
    plafond_bereikt: boolean;
    bedrag: number;
  };

// ─── Medewerker ───────────────────────────────────────────────────────────────

export const fetchMedewerkerVpStatus = async (): Promise<MedewerkerVpStatus> =>
  (await api.get('/vertrouwenspersoon/medewerker/status')).data.data;

export const stuurHulpoproep = async (bericht: string, professional_id?: string | null) =>
  (await api.post('/vertrouwenspersoon/medewerker/hulpoproep', { bericht, professional_id: professional_id ?? null })).data.data as {
    conversation_id: string;
  };

// ─── Hulpfuncties ─────────────────────────────────────────────────────────────

/** Eén GPS-fix met hoge nauwkeurigheid; weigert als de browser geen toegang krijgt. */
export function huidigePositie(): Promise<Positie> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Dit apparaat ondersteunt geen locatiebepaling.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, nauwkeurigheid: p.coords.accuracy }),
      (e) =>
        reject(
          new Error(
            e.code === e.PERMISSION_DENIED
              ? 'Locatietoegang geweigerd. Sta locatie toe voor BeLagom in uw browserinstellingen.'
              : 'Uw locatie kon niet bepaald worden. Probeer het opnieuw.'
          )
        ),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  });
}

/** 8_100_000 → "2 u 15 min" */
export function duurTekst(ms: number): string {
  const totaalMin = Math.floor(ms / 60_000);
  const u = Math.floor(totaalMin / 60);
  const m = totaalMin % 60;
  return u > 0 ? `${u} u ${String(m).padStart(2, '0')} min` : `${m} min`;
}

export const euro = (n: number) => n.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
